/**
 * LLM tools for account data.
 * - list_accounts: flat list of accounts with balances and basic types.
 * - account_info: uniform grouped shape (groups + accounts) with optional grouping by institution or type,
 *   net totals per group, institution names (manual accounts labeled "Manual"), and for credit/loan accounts
 *   lending fields aligned with the transactions account info card (statement amounts, due dates, limits, rates).
 *   Account IDs are included for internal use—LLMs should never surface them directly to users.
 */
import { accountsSelectors, accountsUtils } from 'app-components/flux/accounts';
import type { Account } from 'app-components/flux/accounts/accountsTypes';
import { selectIsAiBusinessToolsEnabled } from 'app-components/flux/ai-chat/aiChatSelectors';
import { businessSelectors } from 'app-components/flux/business';
import { getBalanceForAccountId } from 'app-components/flux/calculated-balances/balanceSelectors';
import { institutionLoginsSelectors } from 'app-components/flux/institution-logins';
import { institutionsSelectors } from 'app-components/flux/institutions';
import { getLogger } from 'app-components/utils/core';
import store from 'app-components/utils/redux-store';
import type RootState from 'app-components/utils/redux-store/rootState';

import { safeParse } from '../../aiChatUtils';
import { returnToolResponse } from '../llmToolsFramework';
import type { ToolMessage } from '../llmToolsTypes';
import { toFiniteNumberOrNull } from '../utils';

const log = getLogger('llmAccountsTools');

type GroupBy = 'none' | 'institution' | 'type';

/** Credit/loan-only snapshot; mirrors data shown on the web account info card where applicable. */
type AccountLendingDetails = {
  statementDueAmount: number | null;
  statementMinimumPayment: number | null;
  /** ISO timestamp for payment due date (same source as the card's "Due date"). */
  paymentDueAt: string | null;
  statementCloseAt: string | null;
  statementEndAt: string | null;
  statementDueDate: string | null;
  statementPastDueAmount: number | null;
  statementLastPaymentAmount: number | null;
  statementLastPaymentAt: string | null;
  statementCloseBalance: number | null;
  creditLimit: number | null;
  /** Positive dollars "used" consistent with the card: -onlineBalance when online balance is present. */
  creditUsed: number | null;
  availableCredit: number | null;
  creditUsedPercent: number | null;
  /** Purchase/balance interest rate as a percentage when provided by the institution. */
  purchaseInterestRatePercent: number | null;
  cashAdvanceInterestRatePercent: number | null;
  /** Annual fee in account currency when provided (field name matches the data model). */
  annualFeeAmount: number | null;
  /** Scheduled minimum payment on the credit account record when distinct from statement minimum. */
  scheduledMinimumPayment: number | null;
  rewardsType: string | null;
  cardType: string | null;
};

function buildLendingDetails(account: Account): AccountLendingDetails | undefined {
  if (!accountsUtils.isLoanAccount(account) && account.type !== 'CREDIT') return undefined;

  const creditLimit = toFiniteNumberOrNull(account.creditLimit) || null;
  const onlineBalance = toFiniteNumberOrNull(account.onlineBalance);
  const creditUsed = onlineBalance !== null ? -onlineBalance : null;
  const availableCredit = creditLimit !== null && creditUsed !== null ? creditLimit - creditUsed : null;
  const creditUsedPercent =
    creditLimit !== null && creditUsed !== null
      ? Math.max(0, Math.round((creditUsed / creditLimit) * 1000) / 10)
      : null;

  return {
    statementDueAmount: toFiniteNumberOrNull(account.statementDueAmount),
    statementMinimumPayment: toFiniteNumberOrNull(account.statementMinPayment),
    paymentDueAt: typeof account.statementDueAt === 'string' ? account.statementDueAt : null,
    statementCloseAt: typeof account.statementCloseAt === 'string' ? account.statementCloseAt : null,
    statementEndAt: typeof account.statementEndAt === 'string' ? account.statementEndAt : null,
    statementDueDate: typeof account.statementDueDate === 'string' ? account.statementDueDate : null,
    statementPastDueAmount: toFiniteNumberOrNull(account.statementPastDueAmount),
    statementLastPaymentAmount: toFiniteNumberOrNull(account.statementLastPaymentAmount),
    statementLastPaymentAt: typeof account.statementLastPaymentAt === 'string' ? account.statementLastPaymentAt : null,
    statementCloseBalance: toFiniteNumberOrNull(account.statementCloseBalance),
    creditLimit,
    creditUsed,
    availableCredit,
    creditUsedPercent,
    purchaseInterestRatePercent: toFiniteNumberOrNull(account.interestRate),
    cashAdvanceInterestRatePercent: toFiniteNumberOrNull(account.cashInterestRate),
    annualFeeAmount: toFiniteNumberOrNull(account.annualFee),
    scheduledMinimumPayment: toFiniteNumberOrNull(account.minPayment),
    rewardsType: typeof account.rewardsType === 'string' ? account.rewardsType : null,
    cardType: typeof account.cardType === 'string' ? account.cardType : null,
  };
}

type AccountInfoEntry = {
  id: string;
  name?: string | null;
  balance: number | null;
  type?: Account['type'];
  subType?: Account['subType'];
  currency?: string | null;
  isClosed: boolean;
  institutionId: string | null;
  institutionName: string;
  institutionLoginId?: string | null;
  businessId?: string | null;
  businessName?: string | null;
  /** Present for CREDIT and LOAN accounts when the model exposes lending fields. */
  lendingDetails?: AccountLendingDetails;
};

const resolveAccountBusiness = (
  account: Account,
  businessesById: ReturnType<typeof businessSelectors.getResourcesById>,
): Pick<AccountInfoEntry, 'businessId' | 'businessName'> => {
  const businessId = accountsUtils.normalizeBusinessIdForUsage(account.businessId) ?? null;
  return { businessId, businessName: businessId ? (businessesById.get(businessId)?.name ?? null) : null };
};

type AccountInfoGroup = {
  groupKey: string;
  groupLabel: string;
  totalBalance: number;
  accounts: AccountInfoEntry[];
};

export const accountTools = {
  /*
  list_accounts: {
    definition: {
      name: 'list_accounts',
      description: `Returns every account in the user's file along with its latest balance, high-level type, and subtype. Use this to answer questions about what accounts the user has, how many there are per type, or to locate a specific account's balance. No parameters are required. NEVER show account IDs to the user - always display account names only.`,
      parameters: {
        type: 'object' as const,
        properties: {},
        required: [],
      },
    },
    handler: (msg: ToolMessage) => {
      const state = store?.getState() as RootState | undefined;

      if (!state) {
        log.error('llmTools/accounts: Redux store is not initialized.');
        return returnToolResponse(msg, { accounts: [], message: 'Accounts are unavailable right now.' });
      }

      const accountsById = accountsSelectors.getAccountsById(state);

      if (!accountsById || accountsById.size === 0) {
        log.debug('llmTools/accounts: No accounts found.');
        return returnToolResponse(msg, { accounts: [], message: 'No accounts found.' });
      }

      const accounts = accountsById
        .valueSeq()
        .map((account) => {
          const accountId = account.id;

          if (!accountId) {
            return undefined;
          }

          const balance = getBalanceForAccountId(state, accountId);

          return {
            id: accountId,
            name: account.name,
            balance,
            type: account.type,
            subType: account.subType,
            currency: account.currency,
            isClosed: Boolean(account.isClosed),
          };
        })
        .filter((account): account is NonNullable<typeof account> => Boolean(account))
        .toArray();

      log.debug('llmTools/accounts: Retrieved accounts.', { count: accounts.length });

      return returnToolResponse(msg, { accounts });
    },
  },
  */

  account_info: {
    definition: {
      name: 'account_info',
      description:
        'Returns account data in grouped form. Use groupBy="none" for a single group of all accounts (net worth total), ' +
        'groupBy="institution" to see accounts grouped by financial institution (including manual accounts labeled "Manual"), ' +
        'or groupBy="type" to group by account type. includeClosed controls whether closed accounts are included (default true). ' +
        'For CREDIT and LOAN accounts, lendingDetails includes statement balances, payment due date (paymentDueAt ISO), ' +
        'statement close/end timestamps when present, credit limit, used/available credit, usage percent, purchase and cash-advance ' +
        'rate percentages when reported, annual fee, rewards/card type, and related statement fields. ' +
        'For business accounts, businessName is the business the account is assigned to (its account purpose); ' +
        'it is null for personal accounts. Never display account or business IDs ' +
        'to end users—use account names. Be concise and brief in your response.',
      parameters: {
        type: 'object' as const,
        properties: {
          groupBy: {
            type: 'string' as const,
            enum: ['none', 'institution', 'type'],
            description: 'How to group accounts. Defaults to "none".',
          },
          includeClosed: {
            type: 'boolean' as const,
            description: 'Whether to include closed accounts. Defaults to true.',
          },
        },
        required: [],
      },
    },
    handler: (msg: ToolMessage) => {
      const args = safeParse(msg.arguments) || {};
      const groupBy: GroupBy = args.groupBy === 'institution' || args.groupBy === 'type' ? args.groupBy : 'none';
      const includeClosed = args.includeClosed !== undefined ? Boolean(args.includeClosed) : true;

      const state = store?.getState() as RootState | undefined;

      if (!state) {
        log.error('llmTools/account_info: Redux store is not initialized.');
        return returnToolResponse(msg, { groups: [], message: 'Accounts are unavailable right now.' });
      }

      const accountsById = accountsSelectors.getAccountsById(state);

      if (!accountsById || accountsById.size === 0) {
        log.debug('llmTools/account_info: No accounts found.');
        return returnToolResponse(msg, { groups: [], message: 'No accounts found.' });
      }

      const institutionLoginsById = institutionLoginsSelectors.getInstitutionLoginsById(state);
      const institutionsById = institutionsSelectors.getInstitutionsById(state);
      const businessesById = selectIsAiBusinessToolsEnabled(state) ? businessSelectors.getResourcesById(state) : null;

      // Step 1: Normalize accounts into a flat list with balances and institution metadata.
      const normalizedAccounts: AccountInfoEntry[] = accountsById
        .valueSeq()
        .reduce<AccountInfoEntry[]>((list: AccountInfoEntry[], account: Account) => {
          if (!account?.id) {
            return list;
          }

          if (!includeClosed && account.isClosed) {
            return list;
          }

          const institutionLoginId = account.institutionLoginId ?? null;
          const institutionLogin =
            institutionLoginId !== null
              ? institutionLoginsById.get(institutionLoginId)
              : institutionLoginsById.get('0');
          const institutionId = institutionLogin?.institutionId ?? null;
          const isManualInstitution = institutionId === null || institutionId === '0';

          const institutionName = isManualInstitution
            ? 'Manual'
            : institutionsById.get(institutionId || '')?.name || institutionLogin?.name || 'Unknown institution';

          const balance = getBalanceForAccountId(state, account.id);

          const lendingDetails = buildLendingDetails(account);

          list.push({
            id: account.id,
            name: account.name,
            balance: balance ?? null,
            type: account.type,
            subType: account.subType,
            currency: account.currency,
            isClosed: Boolean(account.isClosed),
            institutionId: institutionId ?? null,
            institutionName,
            institutionLoginId,
            ...(businessesById ? resolveAccountBusiness(account, businessesById) : {}),
            ...(lendingDetails !== undefined ? { lendingDetails } : {}),
          });

          return list;
        }, []);

      const buildGroup = (groupKey: string, groupLabel: string, accounts: AccountInfoEntry[]): AccountInfoGroup => {
        const totalBalance = accounts.reduce((sum, account) => sum + (account.balance ?? 0), 0);
        return {
          groupKey,
          groupLabel,
          totalBalance,
          accounts,
        };
      };

      if (normalizedAccounts.length === 0) {
        return returnToolResponse(msg, { groups: [], message: 'No accounts found.' });
      }

      if (groupBy === 'institution') {
        // Step 2a: Group by institution (including manual/unknown).
        const groupsMap = normalizedAccounts.reduce<Map<string, AccountInfoGroup>>((map, account) => {
          const institutionKey = account.institutionId ?? '0';
          const existingGroup = map.get(institutionKey);
          const accounts = existingGroup ? [...existingGroup.accounts, account] : [account];
          const label = existingGroup?.groupLabel ?? account.institutionName;
          map.set(institutionKey, buildGroup(institutionKey, label, accounts));
          return map;
        }, new Map());

        return returnToolResponse(msg, { groups: Array.from(groupsMap.values()) });
      }

      if (groupBy === 'type') {
        // Step 2b: Group by account type.
        const groupsMap = normalizedAccounts.reduce<Map<string, AccountInfoGroup>>((map, account) => {
          const typeKey = account.type ?? 'UNKNOWN';
          const existingGroup = map.get(typeKey);
          const accounts = existingGroup ? [...existingGroup.accounts, account] : [account];
          map.set(typeKey, buildGroup(typeKey, typeKey, accounts));
          return map;
        }, new Map());

        return returnToolResponse(msg, { groups: Array.from(groupsMap.values()) });
      }

      // Step 2c: No grouping requested—return a single aggregate group.
      const allGroup = buildGroup('all', 'All Accounts', normalizedAccounts);
      return returnToolResponse(msg, { groups: [allGroup] });
    },
  },
};
